import React, { useState } from 'react';
import { Chip, IconButton, InputAdornment, TextField } from '@mui/material';
import AddCircleIcon from '@mui/icons-material/AddCircle';
import { styled } from '@mui/system';

const DivWrapper = styled('div')({
  width: '100%',
});

const DivChipContainer = styled('div')(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  alignItems: 'center',
  padding: theme.spacing(1),
  marginTop: theme.spacing(1),
}));

const ChipStyled = styled(Chip)(({ theme }) => ({
  margin: theme.spacing(0.5),
}));

const InputAdornmentStyled = styled(InputAdornment)(({ theme }) => ({
  marginRight: theme.spacing(-2),
  '&:hover': {
    backgroundColor: 'transparent',
  },
}));

interface TagInputProps {
  readOnly?: boolean;
  setTags: (tags: string[]) => void;
  tags: string[];
  cypressId?: string;
}

const TagInput: React.FC<TagInputProps> = ({ readOnly, setTags, tags, cypressId }) => {
  const [inputValue, setInputValue] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter') {
      handleAddTag();
      event.preventDefault();
    }
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setInputValue(event.target.value);
    if (errorMessage) {
      setErrorMessage('');
    }
  };

  const handleAddTag = () => {
    if (inputValue.trim()) {
      if (!tags.includes(inputValue.trim())) {
        setTags([...tags, inputValue.trim()]);
        setInputValue('');
        setErrorMessage('');
      } else {
        setErrorMessage('Tag already exists.');
      }
    }
  };

  const handleDelete = (tagToDelete: string) => {
    setTags(tags.filter((tag) => tag !== tagToDelete));
  };

  return (
    <DivWrapper data-cy={cypressId ? cypressId : 'input-add-new-tag'}>
      {!readOnly && (
        <TextField
          fullWidth
          label="Add a tag"
          onChange={handleInputChange}
          onKeyDown={handleKeyDown}
          size="small"
          value={inputValue}
          variant="outlined"
          error={Boolean(errorMessage)}
          helperText={errorMessage}
          InputProps={{
            endAdornment: (
              <InputAdornmentStyled position="end">
                <IconButton onClick={handleAddTag} data-cy="btn-add-new-tag" disableRipple>
                  <AddCircleIcon />
                </IconButton>
              </InputAdornmentStyled>
            ),
          }}
        />
      )}
      <DivChipContainer>
        {tags.map((tag, index) => (
          <ChipStyled
            data-cy={`tag-chip-${tag}`}
            disabled={readOnly}
            key={index}
            label={tag}
            onDelete={() => handleDelete(tag)}
          />
        ))}
      </DivChipContainer>
    </DivWrapper>
  );
};

export default TagInput;
